import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { usePageTitle } from "@/hooks/usePageTitle";
import { AuthLayout, Field } from "@/pages/auth/AuthLayout";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import api, { apiErrorMessage } from "@/lib/api";

export default function Login() {
  const { user, loading: authLoading, login } = useAuth();
  const navigate = useNavigate();
  usePageTitle("Log In");

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  // Auto-redirect if already logged in
  useEffect(() => {
    if (!authLoading && user) {
      navigate("/dashboard", { replace: true });
    }
  }, [user, authLoading, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!email.trim() || !password) {
      setError("Enter your email and password.");
      return;
    }
    setLoading(true);
    try {
      await login(email.trim(), password);
      const { data } = await api.get("/onboarding");
      toast.success("Welcome back");
      navigate(data.completed ? "/dashboard" : "/onboarding");
    } catch (err) {
      setError(apiErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout
      title="Log in"
      subtitle="Continue building your Content Intelligence workspace."
      footer={
        <div className="space-y-2 text-center text-xs">
          <p className="text-muted-foreground">
            New here?{" "}
            <Link to="/signup" className="font-semibold text-accent hover:underline" data-testid="link-signup">
              Create an account
            </Link>
          </p>
          <p className="text-[11px] text-muted-foreground/80">
            <Link to="/privacy" className="hover:underline">Privacy Policy</Link>
            {" · "}
            <Link to="/terms" className="hover:underline">Terms of Service</Link>
          </p>
        </div>
      }
    >
      <form onSubmit={submit} className="space-y-4" data-testid="login-form">
        <Field label="Email">
          <Input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
            data-testid="login-email"
            autoComplete="email"
            required
          />
        </Field>

        <div className="space-y-1.5">
          <div className="flex items-center justify-between">
            <label className="text-sm font-semibold text-foreground">Password</label>
            <Link
              to="/forgot-password"
              className="text-xs font-medium text-accent hover:underline"
              data-testid="link-forgot-password"
            >
              Forgot password?
            </Link>
          </div>
          <Input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="••••••••"
            data-testid="login-password"
            autoComplete="current-password"
            required
          />
        </div>

        {error && (
          <p className="rounded-xl bg-destructive/5 px-3 py-2 text-sm text-destructive" data-testid="login-error">
            {error}
          </p>
        )}

        <Button
          type="submit"
          disabled={loading}
          data-testid="login-submit"
          className="w-full rounded-full bg-accent text-white hover:bg-accent-hover shadow-sm"
        >
          {loading ? "Logging in…" : "Log in"}
        </Button>
      </form>
    </AuthLayout>
  );
}
